import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, Landmark, Search, Loader2 } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

export default function AllInstituteBanks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filterInst, setFilterInst] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/banks/all-institutes");
        setRows(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const institutes = useMemo(() => {
    const set = new Map();
    rows.forEach((r) => r.institute_name && set.set(r.institute_id, r.institute_name));
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterInst && r.institute_id !== filterInst) return false;
      if (!needle) return true;
      return [
        r.institute_name,
        r.bank_name,
        r.branch,
        r.account_number,
        r.ifsc,
        r.purpose,
        r.account_type,
        r.maker_name,
        r.checker_name,
        r.approver_name,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [rows, q, filterInst]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/banks/all-institutes/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : "institute-bank-accounts.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px] mx-auto" data-testid="all-banks-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Treasury · Consolidated</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">All Institute Bank Accounts</h1>
          <p className="mt-2 text-sm text-[#6b655b]">
            Every bank account entered by an institute admin, consolidated in one view. Trust-level accounts are excluded.
          </p>
        </div>
        <button
          onClick={downloadExcel}
          disabled={downloading || rows.length === 0}
          data-testid="all-banks-download"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-sm"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {downloading ? "Preparing…" : "Download Excel"}
        </button>
      </div>

      <div className="mb-5 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655b]" />
          <input
            type="text"
            data-testid="all-banks-search"
            className={inputCls + " pl-9"}
            placeholder="Search bank, branch, IFSC, signatory…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="all-banks-institute-filter"
          className={inputCls + " md:max-w-xs"}
          value={filterInst}
          onChange={(e) => setFilterInst(e.target.value)}
        >
          <option value="">All institutes ({rows.length})</option>
          {institutes.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="all-banks-empty" className="border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center bg-white">
          <Landmark size={28} className="mx-auto text-[#6b655b] mb-3" />
          <p className="text-sm text-[#6b655b]">
            {rows.length === 0
              ? "No institute bank accounts have been entered yet."
              : "No accounts match the current filters."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="all-banks-table">
            <thead className="bg-[#f9f8f6]">
              <tr>
                {[
                  "Institute",
                  "Bank",
                  "Branch",
                  "A/c No",
                  "IFSC",
                  "Type",
                  "Purpose",
                  "Maker",
                  "Checker",
                  "Approver",
                  "Status",
                ].map((h) => (
                  <th key={h} className="px-3 py-3 text-[10px] tracking-wide uppercase text-[#6b655b] font-semibold text-left whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#efece6]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`all-banks-row-${r.id}`} className="hover:bg-[#f9f8f6]/60">
                  <td className="px-3 py-2.5 text-[#1c1a17] font-medium">{r.institute_name || "—"}</td>
                  <td className="px-3 py-2.5 text-[#1c1a17]">
                    {r.bank_name}
                    {r.account_status === "Active" ? null : (
                      <span className="ml-2 text-[10px] text-[#9e2a2b]">[{r.account_status}]</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[#6b655b]">{r.branch || "—"}</td>
                  <td className="px-3 py-2.5 text-[#1c1a17] font-mono text-xs">{r.account_number || "—"}</td>
                  <td className="px-3 py-2.5 text-[#6b655b] font-mono text-xs">{r.ifsc || "—"}</td>
                  <td className="px-3 py-2.5 text-[#6b655b]">{r.account_type || "—"}</td>
                  <td className="px-3 py-2.5 text-[#6b655b]">{r.purpose || "—"}</td>
                  <td className="px-3 py-2.5 text-[#6b655b]">
                    {r.maker_name ? (
                      <>
                        <div>{r.maker_name}</div>
                        {r.maker_designation ? (
                          <div className="text-[10px] text-[#9e9a93]">{r.maker_designation}</div>
                        ) : null}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-[#6b655b]">
                    {r.checker_name ? (
                      <>
                        <div>{r.checker_name}</div>
                        {r.checker_designation ? (
                          <div className="text-[10px] text-[#9e9a93]">{r.checker_designation}</div>
                        ) : null}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-[#6b655b]">
                    {r.approver_name ? (
                      <>
                        <div>{r.approver_name}</div>
                        {r.approver_designation ? (
                          <div className="text-[10px] text-[#9e9a93]">{r.approver_designation}</div>
                        ) : null}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
                      r.account_status === "Active"
                        ? "bg-[#234333]/10 text-[#234333] border-[#234333]/30"
                        : r.account_status === "Closed"
                        ? "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30"
                        : "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/30"
                    }`}>
                      {r.account_status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
